import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { formatDisplayDate } from '../lib/dateFormatting';
import { FiBarChart2, FiBriefcase, FiUserX, FiUsers } from 'react-icons/fi';
import { buildCompactTimelineChartOptions } from './compactTimelineChart';
import {
  BLS_QCEW_DATA_GUIDE_URL,
  buildIndustryTrendChart,
} from './industryTrendChart';
import './LaborMarketSnapshotSection.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
);

const METRIC_ORDER = [
  { key: 'unemployment_rate', title: 'Unemployment Rate', tone: 'rate', icon: FiBarChart2 },
  { key: 'employment_total', title: 'Employed Residents', tone: 'employment', icon: FiBriefcase },
  { key: 'labor_force_total', title: 'Labor Force', tone: 'labor-force', icon: FiUsers },
  { key: 'unemployment_total', title: 'Unemployed Residents', tone: 'unemployed', icon: FiUserX },
];
const BLS_LAUS_DOCS_URL = 'https://www.bls.gov/lau/';
const SHOW_LABOR_METRIC_CARDS = false;

function formatMonthYear(value) {
  return formatDisplayDate(value, { month: 'short', year: 'numeric' });
}

function formatCompactIntegerLabel(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: numeric >= 100000 ? 1 : 0,
  }).format(numeric);
}

function formatFullIntegerLabel(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(numeric);
}

function sortTooltipItemsByVisualOrder(left, right) {
  const leftY = Number(left?.element?.y);
  const rightY = Number(right?.element?.y);
  if (!Number.isFinite(leftY) || !Number.isFinite(rightY)) return 0;
  return leftY - rightY;
}

function resolvePeopleAxisSuggestedMin(seriesValues) {
  const numericValues = seriesValues.filter((value) => Number.isFinite(Number(value))).map(Number);
  if (!numericValues.length) return undefined;

  const minValue = Math.min(...numericValues);
  const latestValue = numericValues[numericValues.length - 1];
  const maxValue = Math.max(...numericValues);
  const needsLowerPadding = minValue <= 49500 || latestValue <= 49500;
  if (!needsLowerPadding) return undefined;

  const range = Math.max(maxValue - minValue, 1000);
  const padding = Math.max(500, Math.round(range * 0.12));
  return Math.floor((minValue - padding) / 1000) * 1000;
}

function buildUnemploymentRateChart(snapshot) {
  const series = snapshot?.metrics?.unemployment_rate;
  const benchmarkSeries = snapshot?.unemploymentRateBenchmarks || {};
  const metroObservations = Array.isArray(series?.observations) ? series.observations : [];
  const stateObservations = Array.isArray(benchmarkSeries?.state?.observations)
    ? benchmarkSeries.state.observations
    : [];
  const usObservations = Array.isArray(benchmarkSeries?.us?.observations)
    ? benchmarkSeries.us.observations
    : [];
  const periods = Array.from(new Set([
    ...metroObservations.map((item) => item.period),
    ...stateObservations.map((item) => item.period),
    ...usObservations.map((item) => item.period),
  ])).sort();
  const metroValuesByPeriod = new Map(
    metroObservations.map((item) => [item.period, Number(item.value)]),
  );
  const stateValuesByPeriod = new Map(
    stateObservations.map((item) => [item.period, Number(item.value)]),
  );
  const usValuesByPeriod = new Map(
    usObservations.map((item) => [item.period, Number(item.value)]),
  );
  const hasBenchmarks = stateObservations.length >= 2 || usObservations.length >= 2;

  const datasets = [
    {
      label: snapshot?.marketName || 'Market',
      data: periods.map((period) => metroValuesByPeriod.get(period) ?? null),
      borderColor: '#1d4ed8',
      backgroundColor: 'rgba(29, 78, 216, 0.10)',
      fill: !hasBenchmarks,
      borderWidth: 2.2,
      pointRadius: hasBenchmarks ? 0 : 2,
      pointHoverRadius: 3,
      pointBackgroundColor: '#1d4ed8',
      pointBorderWidth: 0,
      spanGaps: true,
      tension: 0.28,
    },
  ];

  if (stateObservations.length >= 2) {
    datasets.push({
      label: benchmarkSeries.state.label || 'State',
      data: periods.map((period) => stateValuesByPeriod.get(period) ?? null),
      borderColor: '#0f766e',
      backgroundColor: 'rgba(15, 118, 110, 0.08)',
      fill: false,
      borderWidth: 2,
      borderDash: [8, 6],
      pointRadius: 0,
      pointHoverRadius: 3,
      pointBackgroundColor: '#0f766e',
      pointBorderWidth: 0,
      spanGaps: true,
      tension: 0.24,
    });
  }

  if (usObservations.length >= 2) {
    datasets.push({
      label: benchmarkSeries.us.label || 'U.S.',
      data: periods.map((period) => usValuesByPeriod.get(period) ?? null),
      borderColor: '#c2410c',
      backgroundColor: 'rgba(194, 65, 12, 0.08)',
      fill: false,
      borderWidth: 1.9,
      borderDash: [3, 5],
      pointRadius: 0,
      pointHoverRadius: 3,
      pointBackgroundColor: '#c2410c',
      pointBorderWidth: 0,
      spanGaps: true,
      tension: 0.22,
    });
  }

  return {
    data: {
      labels: periods.map((period) => formatMonthYear(period)),
      datasets,
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          display: datasets.length > 1,
          position: 'bottom',
          labels: {
            color: '#64748b',
            boxWidth: 18,
            boxHeight: 2,
            usePointStyle: false,
          },
        },
        tooltip: {
          itemSort: sortTooltipItemsByVisualOrder,
          callbacks: {
            label(context) {
              const value = Number(context.parsed.y);
              return `${context.dataset.label}: ${value.toFixed(1)}%`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#64748b',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            color: 'rgba(148, 163, 184, 0.18)',
          },
          ticks: {
            color: '#64748b',
            callback(value) {
              return `${Number(value).toFixed(1)}%`;
            },
          },
        },
      },
    },
  };
}

function buildEmploymentVsLaborForceChart(
  employmentSeries,
  laborForceSeries,
  unemploymentSeries,
) {
  const employmentObservations = Array.isArray(employmentSeries?.observations)
    ? employmentSeries.observations
    : [];
  const laborForceObservations = Array.isArray(laborForceSeries?.observations)
    ? laborForceSeries.observations
    : [];
  const unemploymentObservations = Array.isArray(unemploymentSeries?.observations)
    ? unemploymentSeries.observations
    : [];

  const periods = Array.from(new Set([
    ...employmentObservations.map((item) => item.period),
    ...laborForceObservations.map((item) => item.period),
  ])).sort();

  const employmentValuesByPeriod = new Map(
    employmentObservations.map((item) => [item.period, Number(item.value)]),
  );
  const laborForceValuesByPeriod = new Map(
    laborForceObservations.map((item) => [item.period, Number(item.value)]),
  );
  const unemploymentValuesByPeriod = new Map(
    unemploymentObservations.map((item) => [item.period, Number(item.value)]),
  );
  const employmentValues = periods.map((period) => employmentValuesByPeriod.get(period) ?? null);
  const laborForceValues = periods.map((period) => laborForceValuesByPeriod.get(period) ?? null);
  const suggestedMin = resolvePeopleAxisSuggestedMin([
    ...employmentValues,
    ...laborForceValues,
  ]);

  return {
    data: {
      labels: periods.map((period) => formatMonthYear(period)),
      datasets: [
        {
          label: 'Employed Residents',
          data: employmentValues,
          borderColor: '#1d4ed8',
          backgroundColor: 'rgba(29, 78, 216, 0.08)',
          fill: false,
          borderWidth: 2.2,
          pointRadius: 0,
          pointHoverRadius: 3,
          pointBackgroundColor: '#1d4ed8',
          pointBorderWidth: 0,
          spanGaps: true,
          tension: 0.28,
        },
        {
          label: 'Labor Force',
          data: laborForceValues,
          borderColor: '#0f766e',
          backgroundColor: 'rgba(15, 118, 110, 0.08)',
          fill: false,
          borderWidth: 2,
          borderDash: [8, 6],
          pointRadius: 0,
          pointHoverRadius: 3,
          pointBackgroundColor: '#0f766e',
          pointBorderWidth: 0,
          spanGaps: true,
          tension: 0.24,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            color: '#64748b',
            boxWidth: 18,
            boxHeight: 2,
            usePointStyle: false,
          },
        },
        tooltip: {
          itemSort: sortTooltipItemsByVisualOrder,
          callbacks: {
            label(context) {
              return `${context.dataset.label}: ${formatFullIntegerLabel(context.parsed.y)}`;
            },
            afterBody(tooltipItems) {
              const firstItem = Array.isArray(tooltipItems) ? tooltipItems[0] : null;
              const period = periods[firstItem?.dataIndex];
              const unemployedResidents = unemploymentValuesByPeriod.get(period);
              if (!Number.isFinite(unemployedResidents)) return [];
              return [`Unemployed Residents: ${formatFullIntegerLabel(unemployedResidents)}`];
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            color: '#64748b',
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 8,
          },
        },
        y: {
          beginAtZero: false,
          suggestedMin,
          grid: {
            color: 'rgba(148, 163, 184, 0.18)',
          },
          ticks: {
            color: '#64748b',
            callback(value) {
              return formatCompactIntegerLabel(value);
            },
          },
        },
      },
    },
  };
}

export function ChartDescription({ question, children, href, label, compact = false }) {
  return (
    <div className="labor-snapshot__chart-copy">
      <p className="labor-snapshot__question">
        <span className="labor-snapshot__question-label">Question:</span>
        <span className="labor-snapshot__question-text">{question}</span>
        {compact ? (
          <a
            className="labor-snapshot__doc-link labor-snapshot__question-link"
            href={href}
            target="_blank"
            rel="noreferrer"
          >
            {label}
          </a>
        ) : null}
      </p>
      {!compact ? (
        <p className="labor-snapshot__chart-description">
          {children}{' '}
          <a
            className="labor-snapshot__doc-link"
            href={href}
            target="_blank"
            rel="noreferrer"
          >
            {label}
          </a>
        </p>
      ) : null}
    </div>
  );
}

export function LaborTabbedChartDemo({
  loading,
  error,
  snapshot,
  variant = 'standard',
  headerControls = null,
  industryLoading = false,
  industryError = '',
  industryPayload = null,
  onIndustryRequested = null,
  onRetry = null,
}) {
  const compact = variant === 'compact';
  const [activeTab, setActiveTab] = useState('unemployment');
  const metrics = snapshot?.metrics || {};
  const unemploymentRate = metrics.unemployment_rate;
  const employmentTotal = metrics.employment_total;
  const laborForceTotal = metrics.labor_force_total;
  const unemploymentTotal = metrics.unemployment_total;
  const unemploymentChart = unemploymentRate && Array.isArray(unemploymentRate.observations) && unemploymentRate.observations.length >= 2
    ? buildUnemploymentRateChart(snapshot)
    : null;
  const employmentChart = employmentTotal
    && laborForceTotal
    && Array.isArray(employmentTotal.observations)
    && Array.isArray(laborForceTotal.observations)
    && employmentTotal.observations.length >= 2
    && laborForceTotal.observations.length >= 2
    ? buildEmploymentVsLaborForceChart(employmentTotal, laborForceTotal, unemploymentTotal)
    : null;
  const laborTabs = [
    {
      key: 'unemployment',
      label: 'Unemployment Rate',
      question: 'How is this market tracking versus state and U.S. unemployment?',
      compactQuestion: 'How does unemployment compare with state and U.S.?',
      description: 'Metro, state, and U.S. rates are not seasonally adjusted. Compare year over year, not month to month.',
      chart: unemploymentChart,
      empty: 'Not enough unemployment-rate history is loaded yet to draw the 24-month trend.',
    },
    {
      key: 'employment',
      label: 'Employment + Labor Force',
      question: 'Are people staying in the workforce, and are more people actually employed?',
      compactQuestion: 'Are employment and labor force growing together?',
      description: 'BLS LAUS tracks metro labor-force and employment counts by month. Rising employment alongside a stable or growing labor force is a healthier signal than employment growth that only comes from people leaving the workforce.',
      chart: employmentChart,
      empty: 'Not enough employment and labor-force history is loaded yet to draw the trend.',
    },
  ];
  const industryChart = industryPayload?.status === 'ready' && industryPayload?.series?.length
    ? buildIndustryTrendChart(industryPayload)
    : null;
  const tabs = compact ? [
    ...laborTabs,
    {
      key: 'industry',
      label: 'Industry Employment Trends',
      question: 'Are this market’s largest industries growing or shrinking?',
      compactQuestion: 'Are the largest industries growing or shrinking?',
      chart: industryChart,
      empty: 'Industry trend history is not available for this market.',
      href: BLS_QCEW_DATA_GUIDE_URL,
      sourceLabel: 'BLS QCEW data guide',
    },
  ] : laborTabs;
  const activeIndex = Math.max(0, tabs.findIndex((tab) => tab.key === activeTab));
  const active = tabs[activeIndex];
  const activeIsIndustry = active.key === 'industry';
  const activeLoading = activeIsIndustry ? industryLoading : loading;
  const activeError = activeIsIndustry ? industryError : error;

  function selectTab(tab) {
    setActiveTab(tab.key);
    if (tab.key === 'industry') onIndustryRequested?.();
  }

  function selectRelativeTab(direction) {
    const nextIndex = (activeIndex + direction + tabs.length) % tabs.length;
    selectTab(tabs[nextIndex]);
  }

  return (
    <article
      className={`${compact ? '' : 'research-detail-card '}labor-tabbed-demo${compact ? ' labor-tabbed-demo--compact' : ''}`}
      aria-labelledby="labor-tabbed-demo-title"
    >
      <div className="labor-tabbed-demo__header">
        <div>
          <span className="labor-tabbed-demo__eyebrow">Layout demo</span>
          <h2 id="labor-tabbed-demo-title">Labor Market Trends</h2>
        </div>
        {headerControls}
        {!loading && !error && snapshot?.status !== 'empty' ? (
          <div className="labor-snapshot__status-group" aria-label="Latest labor data summary">
            <span className="labor-snapshot__status">
              Unemployment rate: {unemploymentRate?.latest?.displayValue || 'Unavailable'}
            </span>
            <span className="labor-snapshot__status">
              {unemploymentRate?.latest?.periodLabel || snapshot?.source?.latestPeriodLabel || 'Latest period unavailable'}
            </span>
          </div>
        ) : null}
      </div>

      {(compact || (!loading && !error && snapshot?.status !== 'empty')) ? (
        <div className="labor-tabbed-demo__tabs" role="tablist" aria-label="Labor market trend charts">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              id={`labor-demo-tab-${tab.key}`}
              type="button"
              role="tab"
              aria-selected={active.key === tab.key}
              aria-controls="labor-demo-panel"
              tabIndex={active.key === tab.key ? 0 : -1}
              className={`labor-tabbed-demo__tab${active.key === tab.key ? ' is-active' : ''}`}
              onClick={() => selectTab(tab)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowRight') {
                  event.preventDefault();
                  selectRelativeTab(1);
                } else if (event.key === 'ArrowLeft') {
                  event.preventDefault();
                  selectRelativeTab(-1);
                }
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}

      <div
        id="labor-demo-panel"
        className={`labor-tabbed-demo__panel${activeLoading ? ' is-loading' : ''}`}
        role="tabpanel"
        aria-labelledby={`labor-demo-tab-${active.key}`}
      >
        {activeLoading ? (
          <div
            className={compact ? 'labor-tabbed-demo__inline-loading' : 'research-detail-state'}
            aria-label="Loading labor market trend"
          >
            Loading labor market trend…
          </div>
        ) : null}
        {!activeLoading && activeError ? (
          <div className="research-detail-state research-detail-state--error">
            {activeError}
            {typeof onRetry === 'function' ? <button type="button" className="research-detail-state__retry" onClick={onRetry}>Try again</button> : null}
          </div>
        ) : null}
        {!activeLoading && !activeError && !activeIsIndustry && snapshot?.status === 'empty' ? (
          <div className="labor-snapshot__empty">
            <p>{snapshot?.emptyMessage || 'BLS labor-market observations have not been loaded for this market yet.'}</p>
          </div>
        ) : null}
        {!activeLoading && !activeError && (activeIsIndustry || snapshot?.status !== 'empty') ? (
          <>
            <ChartDescription
              question={compact ? active.compactQuestion || active.question : active.question}
              href={active.href || BLS_LAUS_DOCS_URL}
              label={active.sourceLabel || 'BLS LAUS docs'}
              compact={compact}
            >
              {active.description}
            </ChartDescription>
            {active.chart ? (
              <div className="labor-tabbed-demo__chart">
                <Line
                  data={active.chart.data}
                  options={compact
                    ? buildCompactTimelineChartOptions(active.chart.options, { tightenBottomSpacing: true })
                    : active.chart.options}
                />
              </div>
            ) : (
              <div className="labor-snapshot__chart-empty">{active.empty}</div>
            )}
          </>
        ) : null}
      </div>
    </article>
  );
}

function LaborMetricCard({ config, series }) {
  const isUnavailable = !series;
  const Icon = config.icon;
  return (
    <article
      className={`labor-snapshot-card labor-snapshot-card--${config.tone}${isUnavailable ? ' labor-snapshot-card--unavailable' : ''}`}
    >
      <div className="labor-snapshot-card__icon-wrap" aria-hidden="true">
        <Icon className="labor-snapshot-card__icon" />
      </div>
      <div className="labor-snapshot-card__body">
        <div className="labor-snapshot-card__head">
          <span className="labor-snapshot-card__title">{config.title}</span>
          <span className="labor-snapshot-card__period">
            {series?.latest?.periodLabel || 'Not loaded'}
          </span>
        </div>
        <strong className="labor-snapshot-card__value">
          {series?.latest?.displayValue || 'Unavailable'}
        </strong>
        <p className="labor-snapshot-card__delta">
          {series?.yoyChangeLabel || 'Year-ago comparison pending.'}
        </p>
      </div>
    </article>
  );
}

function LaborSnapshotDebugPanel({ snapshot }) {
  const metrics = snapshot?.metrics || {};
  const source = snapshot?.source || {};

  return (
    <details className="labor-snapshot__debug" data-testid="labor-debug-panel">
      <summary>Dev QA Trace</summary>
      <div className="labor-snapshot__debug-meta">
        <span>Market ID: {snapshot?.marketId ?? '—'}</span>
        <span>Region Slug: {snapshot?.marketSlug || '—'}</span>
        <span>Dataset: {source.datasetKey || '—'}</span>
      </div>
      <div className="labor-snapshot__debug-grid">
        {METRIC_ORDER.map((config) => {
          const series = metrics[config.key];
          const trace = series?.trace || {};
          return (
            <article key={config.key} className="labor-snapshot__debug-card">
              <strong>{config.title}</strong>
              <span>Metric Key: {config.key}</span>
              <span>Latest Period: {series?.latest?.periodLabel || '—'}</span>
              <span>Latest Value: {series?.latest?.displayValue || '—'}</span>
              <span>Prior-Year Period: {series?.priorYearSameMonth?.periodLabel || '—'}</span>
              <span>Prior-Year Value: {series?.priorYearSameMonth?.displayValue || '—'}</span>
              <span>YoY: {series?.yoyChangeLabel || '—'}</span>
              <span>Geo: {trace.geographyLevel || '—'} / {trace.geographyCode || '—'}</span>
              <span>BLS Series ID: {trace.seriesId || '—'}</span>
              <span>Measure Code: {trace.measureCode || '—'}</span>
            </article>
          );
        })}
      </div>
    </details>
  );
}

export default function LaborMarketSnapshotSection({ loading, error, snapshot }) {
  const source = snapshot?.source || {};
  const metrics = snapshot?.metrics || {};
  const missingMetrics = Array.isArray(snapshot?.missingMetrics) ? snapshot.missingMetrics : [];
  const showDebugPanel = process.env.NODE_ENV !== 'production';
  const unemploymentRate = metrics.unemployment_rate;
  const employmentTotal = metrics.employment_total;
  const laborForceTotal = metrics.labor_force_total;
  const unemploymentTotal = metrics.unemployment_total;
  const unemploymentChart = unemploymentRate && Array.isArray(unemploymentRate.observations) && unemploymentRate.observations.length >= 2
    ? buildUnemploymentRateChart(snapshot)
    : null;
  const employmentVsLaborForceChart = employmentTotal
    && laborForceTotal
    && Array.isArray(employmentTotal.observations)
    && Array.isArray(laborForceTotal.observations)
    && employmentTotal.observations.length >= 2
    && laborForceTotal.observations.length >= 2
    ? buildEmploymentVsLaborForceChart(employmentTotal, laborForceTotal, unemploymentTotal)
    : null;

  return (
    <article id="labor-market-snapshot-card" className="research-detail-card research-detail-card--labor-snapshot">
      <div className="labor-snapshot__header">
        <div>
          <h2 className="labor-snapshot__title">Labor Market Snapshot</h2>
          <p className="labor-snapshot__subtitle">
            Official BLS LAUS labor-market data for this metro. Values are shown as factual regional context, not as a buy/sell score.
          </p>
        </div>
        {!loading && !error && snapshot?.status !== 'empty' ? (
          <div className="labor-snapshot__status-group" aria-label="Latest labor data summary">
            <span className="labor-snapshot__status">
              Unemployment rate: {unemploymentRate?.latest?.displayValue || 'Unavailable'}
            </span>
            <span className="labor-snapshot__status">
              {unemploymentRate?.latest?.periodLabel || source.latestPeriodLabel || 'Latest period unavailable'}
            </span>
          </div>
        ) : null}
      </div>

      {loading ? (
        <div className="research-detail-state">Loading labor market data…</div>
      ) : null}

      {!loading && error ? (
        <div className="research-detail-state research-detail-state--error">{error}</div>
      ) : null}

      {!loading && !error && snapshot?.status === 'empty' ? (
        <div className="labor-snapshot__empty">
          <p>{snapshot?.emptyMessage || 'BLS labor-market observations have not been loaded for this market yet.'}</p>
        </div>
      ) : null}

      {!loading && !error && snapshot?.status !== 'empty' ? (
        <>
          {missingMetrics.length ? (
            <div className="labor-snapshot__partial-note">
              Showing {METRIC_ORDER.length - missingMetrics.length} of {METRIC_ORDER.length} labor series. Missing cards stay visible as unavailable until their BLS rows are loaded.
            </div>
          ) : null}

          {/* Metric cards are intentionally hidden while the chart-first labor layout is evaluated. */}
          {SHOW_LABOR_METRIC_CARDS ? (
            <div className="labor-snapshot__metrics" data-testid="labor-metric-cards">
              {METRIC_ORDER.map((config) => (
                <LaborMetricCard key={config.key} config={config} series={metrics[config.key]} />
              ))}
            </div>
          ) : null}

          <div className="labor-snapshot__chart-card">
            <div className="labor-snapshot__chart-head">
              <div>
                <h3>Employment vs Labor Force Trend</h3>
                <ChartDescription
                  question="Are people staying in the workforce, and are more people actually employed?"
                  href={BLS_LAUS_DOCS_URL}
                  label="BLS LAUS docs"
                >
                  BLS LAUS tracks metro labor-force and employment counts by month. Rising employment alongside a stable or growing labor force is a healthier signal than employment growth that only comes from people leaving the workforce.
                </ChartDescription>
              </div>
            </div>
            {employmentVsLaborForceChart ? (
              <div className="labor-snapshot__chart-wrap">
                <Line data={employmentVsLaborForceChart.data} options={employmentVsLaborForceChart.options} />
              </div>
            ) : (
              <div className="labor-snapshot__chart-empty">
                Not enough employment and labor-force history is loaded yet to draw the trend.
              </div>
            )}
          </div>

          <div className="labor-snapshot__chart-card">
            <div className="labor-snapshot__chart-head">
              <div>
                <h3>Unemployment Rate Trend</h3>
                <ChartDescription
                  question="How is this market tracking versus state and U.S. unemployment?"
                  href={BLS_LAUS_DOCS_URL}
                  label="BLS LAUS docs"
                >
                  Metro, state, and U.S. rates are not seasonally adjusted. Compare year over year, not month to month. {snapshot?.chartNote || ''}
                </ChartDescription>
              </div>
            </div>
            {unemploymentChart ? (
              <div className="labor-snapshot__chart-wrap">
                <Line data={unemploymentChart.data} options={unemploymentChart.options} />
              </div>
            ) : (
              <div className="labor-snapshot__chart-empty">
                Not enough unemployment-rate history is loaded yet to draw the 24-month trend.
              </div>
            )}
          </div>


        </>
      ) : null}



      {showDebugPanel && !loading && !error && snapshot?.status !== 'empty' ? (
        <LaborSnapshotDebugPanel snapshot={snapshot} />
      ) : null}
    </article>
  );
}
